import React, { createContext, useContext, useState, useEffect } from 'react';
import UserService from '../API/UserService';
import logger from '../utils/logger';
// AuthContext хранит:
//  - isAuthenticated  — есть ли валидный JWT
//  - profile          — данные профиля из user-микросервиса
//  - hasAdminAccess   — есть ли у пользователя доступ к этой админ-панели
//                       (true для owner/teacher/admin, false для обычных user)
//  - accessRole       — 'owner' | 'teacher' | 'admin' | 'none'
//  - accessReason     — текст для показа на странице 401 (только если has_access=false)
//
// Если зашёл обычный user (например, родитель ребёнка с Майнкрафтии) —
// hasAdminAccess=false, ProtectedRoute его не пустит в /main.

const AuthContext = createContext();

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [profile, setProfile] = useState(null);
  const [hasAdminAccess, setHasAdminAccess] = useState(false);
  const [accessRole, setAccessRole] = useState('none');
  const [accessReason, setAccessReason] = useState('');
  const [b2bStatus, setB2bStatus] = useState(null); // 'lead'|'active'|'expired'|null
  const [isLoading, setIsLoading] = useState(true);

  // ВАЖНО: фолбэк по role. Если admin-микросервис недоступен (сеть, 5xx,
  // не настроен proxy-маршрут), но в профиле пользователя стоит role='admin' —
  // пускаем как глобального админа. Эту роль меняет только админ user-сервиса
  // вручную, доверять ей можно. Иначе админ Майнкрафтии не сможет зайти в
  // админ-панель центра, когда у admin-сервиса проблемы.
  const refreshAccess = async () => {
    try {
      const res = await UserService.checkAdminAccess();
      setHasAdminAccess(!!res?.has_access);
      setAccessRole(res?.role || 'none');
      setAccessReason(res?.reason || '');
      setB2bStatus(res?.b2b_status || null);
      return res;
    } catch (e) {
      logger.error('[AuthContext.refreshAccess] checkAdminAccess failed:', e?.message || e);
      // Фолбэк: спросим у user-сервиса свежий профиль и проверим role
      try {
        const data = await UserService.getMyProfile();
        if (data && data.role === 'admin') {
          logger.warn('[AuthContext.refreshAccess] fallback: пускаем как admin по profile.role');
          setProfile(data);
          setHasAdminAccess(true);
          setAccessRole('admin');
          setAccessReason('');
          setB2bStatus(null);
          return { has_access: true, role: 'admin' };
        }
      } catch (profErr) {
        logger.error('[AuthContext.refreshAccess] fallback getMyProfile failed:', profErr?.message || profErr);
      }
      setHasAdminAccess(false);
      setAccessRole('none');
      setAccessReason('Не удалось проверить доступ. Попробуйте позже.');
      setB2bStatus(null);
      return null;
    }
  };

  // Вызывается после логина — синхронно выставляет токен, дальше тянет
  // профиль и доступ.
  // Если getMyProfile упал — не блокируем вход в админку (профиль не критичен
  // для проверки доступа), но логируем для диагностики.
  const login = async (token) => {
    localStorage.setItem('Authorization', token);
    setIsAuthenticated(true);
    try {
      const data = await UserService.getMyProfile();
      setProfile(data);
    } catch (e) {
      logger.error('[AuthContext.login] getMyProfile failed:', e?.message || e);
    }
    return await refreshAccess();
  };

  const logout = () => {
    localStorage.removeItem('Authorization');
    setIsAuthenticated(false);
    setProfile(null);
    setHasAdminAccess(false);
    setAccessRole('none');
    setAccessReason('');
    setB2bStatus(null);
  };

  useEffect(() => {
    const init = async () => {
      const token = localStorage.getItem('Authorization');
      if (token) {
        setIsAuthenticated(true);
        try {
          const data = await UserService.getMyProfile();
          setProfile(data);
        } catch (e) {
          // Не критично — пользователь может зайти и без profile, accessCheck решит.
          logger.error('[AuthContext.init] getMyProfile failed:', e?.message || e);
        }
        await refreshAccess();
      }
      setIsLoading(false);
    };
    init();
  }, []);

  return (
    <AuthContext.Provider value={{
      isAuthenticated, profile, hasAdminAccess, accessRole, accessReason,
      b2bStatus, login, logout, isLoading, refreshAccess,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
